import { CSSProperties, forwardRef, ReactNode, useMemo } from "react";

import classNames from "classnames";

import { DashboardIcon } from "../Icon";
import { DashboardOptionLabel } from "../OptionLabel/DashboardOptionLabel";
import { OptionLabelProps } from "../OptionLabel/types";
import { DashboardTooltip } from "../Tooltip/DashboardTooltip";

import { zeffyRadioClasses, StyledRadio } from "./styles";

import { Info } from "@/icons/outlined";
import { InputVibe } from "@/types/mui";

interface RadioProps extends Pick<OptionLabelProps, "alignContent"> {
  title: string;
  size?: "small" | "medium";
  disabled?: boolean;
  vibe?: InputVibe;
  value?: string;
  label?: string | ReactNode;
  checked?: boolean;
  errorMessage?: string;
  row?: boolean;
  fullWidth?: boolean;
  columns?: number;
  className?: string;
  style?: CSSProperties;
  tooltipText?: string;
}

export const Radio = forwardRef<HTMLButtonElement, RadioProps>(
  (
    {
      alignContent,
      size = "small",
      disabled,
      vibe,
      title,
      value,
      label,
      errorMessage,
      checked,
      row,
      fullWidth,
      columns = 1,
      className,
      style,
      tooltipText,
    },
    ref
  ) => {
    const labelStyle: CSSProperties = useMemo(
      () => ({
        overflowWrap: "anywhere",
        marginTop: 0,
        width: row ? undefined : `${100 / columns}%`,
        ...style,
      }),
      [columns, row, style]
    );

    const labelWithTooltip = (
      <div style={{ display: "flex", alignItems: "center" }}>
        {label || value}
        {tooltipText && (
          <DashboardTooltip title={tooltipText} sx={{ marginLeft: 0.5 }} placement="bottom">
            <DashboardIcon size="small" sx={{ cursor: "pointer" }}>
              <Info />
            </DashboardIcon>
          </DashboardTooltip>
        )}
      </div>
    );

    return (
      <DashboardOptionLabel
        alignContent={alignContent}
        style={labelStyle}
        className={className}
        vibe={vibe}
        data-test="base-radio-group-choice"
        errorMessage={errorMessage}
        label={labelWithTooltip}
        size={size}
        fullWidth={fullWidth}
        disabled={disabled}
      >
        <StyledRadio
          ref={ref}
          classes={{
            root: classNames(zeffyRadioClasses.root, { [zeffyRadioClasses.small]: size === "small" }),
            disabled: zeffyRadioClasses.disabled,
          }}
          color="primary"
          checkedIcon={
            <div
              className={classNames(zeffyRadioClasses.circleContainer, {
                [zeffyRadioClasses.small]: size === "small",
                [zeffyRadioClasses.smallCircleContainer]: size === "small",
              })}
            >
              <div className={zeffyRadioClasses.circle} />
            </div>
          }
          disabled={disabled}
          icon={
            <div
              className={classNames(zeffyRadioClasses.circleContainer, {
                [zeffyRadioClasses.circleContainerForm]: vibe === "form",
                [zeffyRadioClasses.small]: size === "small",
              })}
            />
          }
          title={title}
          disableRipple
          size={size}
          value={value}
          checked={checked}
        />
      </DashboardOptionLabel>
    );
  }
);

Radio.displayName = "Radio";
